import { appClient } from '../api/http';
import { account, onSignedIn } from '../session/session.store';
import { reachState } from '../reach/reach.store';
import { homeViewUrls } from '../../features/home/home.store';
import { containerViewUrls } from '../../features/containers/container.store';
import { tagsListViewUrls } from '../../features/tags/tags.store';
import { tagViewUrls } from '../../features/tags/tag.store';
import { openedKey, openedTimes, type OpenedKind } from './last-opened';
import type { ContextSummary, PagedContexts, PagedProjects, ProjectSummary, TagSummary } from '../api/types';

/**
 * 036 (issue 222, FR-023, research R-010) — "Whenever the application comes to the front with a
 * connection, it MUST fetch home, the contexts list, the projects list, the tickler, the tags list,
 * and the page of every context, project and tag that is not hidden or completed, beginning with
 * those most recently opened on that device. This MUST NOT delay the screen being looked at."
 *
 * **A departure stated up front: this file imports from `features/`, which no other file under
 * `core/` does today.** `allow-list.ts` proves a URL's SHAPE independently of the stores that build
 * it, and the first draft of this file did the same — reconstructed every container and tag URL by
 * hand. That is exactly the "second definition" this repository keeps a guard against (SC-014's own
 * words), and the risk here is sharper than most: the `views` store is keyed by the exact URL
 * STRING, so a fetch-ahead copy that drifts one query parameter out of order from
 * `container.store.ts`'s own `containerViewUrls()` would silently stop the container page it just
 * "warmed" from ever finding what was stored — a defect no allow-list test would catch, because the
 * allow-list only asks whether a shape is permitted, never whether two callers agree on it byte for
 * byte. `containerViewUrls()` and `tagViewUrls()` are exported for exactly this caller, and this file
 * calls them rather than re-deriving; `core/preferences/` already sits under `core/` and reads from
 * nothing this file does not already need.
 *
 * **Only where a service worker controls the page** (`controllingServiceWorker()`). Without a
 * controlling worker there is no precached shell, so the application cannot be launched offline and
 * an unvisited page cannot be reached offline either — the development build `e2e/` serves has none,
 * which is what keeps `e2e/home-scale.spec.ts`'s exact request counts untouched by this whole file.
 *
 * **Two triggers, not the shared beat.** `core/reach/reach.store.ts`'s `subscribeToBeat` exists for
 * "keep trying while something is owed" (the outbox's own send loop) and fires its periodic tick
 * whenever ANY subscriber has work — joining it here would mean a five-second re-fetch of every
 * container and tag on the device for as long as the OUTBOX merely had something waiting, which is
 * not "comes to the front" by any reading of FR-023. This file listens for exactly the two events
 * the requirement names instead: the session becoming `signed-in` (`onSignedIn`, the same edge
 * `core/outbox/outbox.store.ts` wakes on) and the document's own `visibilitychange` to `visible`,
 * gated on `core/reach/`'s own `reachable` — never the raw `navigator.onLine` (FR-003).
 *
 * **Never delays the screen in view.** Both triggers hand off to `requestIdleCallback` (a
 * `setTimeout` fallback where it is missing — jsdom under Vitest, notably) before this file issues
 * its own first request. A screen's own mount effect calls `appClient().get(...)` synchronously
 * inside `useEffect`, which runs, and files its request, before the browser is next idle — so by
 * construction this file's own first request is queued behind the screen's, never ahead of it.
 * Within its own work this file makes at most two requests in flight at once (`CONCURRENCY` below),
 * which is what SC-016's own 100ms budget for the FIRST screen measures.
 *
 * **Stops, part-way if need be, the moment the document hides, reach stops being `reachable`, the
 * practitioner it started for is no longer the one signed in, or a new version of the application
 * starts installing** — checked before every request this file makes, fixed reads and
 * container/tag pages alike, in `shouldContinue()`. Nothing here resumes a partial pass; the next
 * qualifying trigger runs the whole thing again; a page already stored earlier in the same pass is a cheap re-fetch, not a
 * mistake, since a fresher answer is always what FR-023 wants held for next time.
 */

/** How many of this file's own requests may be in flight at once — "one at a time, or at most two". */
const CONCURRENCY = 2;

/**
 * SC-016's own measurement needs a legitimate "fetch-ahead OFF" condition against the identical
 * signed-in, service-worker-controlled profile "fetch-ahead ON" is measured against — nothing else
 * in this file offers one, since `controllingServiceWorker()` being false also means an offline
 * launch is impossible, which is not the comparison SC-016 asks for. Read once, at the top of every
 * run, never written by production code: `e2e-pwa/offline-fetch-ahead-scale.spec.ts` is this key's
 * only writer. **This is a test seam that production code READS**, which nothing else under `src/`
 * has: `OFFLINE_KEY` in `e2e-pwa/offline.ts` is read only by that file's own init script. Setting
 * it in a real browser costs that browser its fetch-ahead and nothing else.
 */
const DISABLE_FOR_MEASUREMENT_KEY = 'completeme.e2e.disableFetchAhead';

function disabledForMeasurement(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISABLE_FOR_MEASUREMENT_KEY) === '1';
  } catch {
    return false;
  }
}

function controllingServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.controller != null;
}

/** What a pass started under, and must still hold for it to issue its next request. */
interface Pass {
  /**
   * The practitioner the pass began under. A pass carried on under another session would ask for
   * pages from the first practitioner's lists, and would hold `running` so the new practitioner's
   * own sign-in trigger was dropped. Stopping on any change of practitioner (a sign-out included)
   * ends it at the next request; a trigger arriving while one request is still in flight is still
   * dropped, and the next time the application comes to the front runs the pass.
   */
  readonly startedFor: number | string;
  /**
   * The controlling worker's registration, whose `installing` and `waiting` are live. **No request
   * while a new version is installing or waiting.** Requests passing through the old worker while a
   * new one installs left Chromium unwilling to activate the new one: the update offer's button
   * posted its message, `skipWaiting()` never resolved, and the page never reloaded — measured at
   * 15 of 60 runs of `offline-update.spec.ts` with this file, 0 of 60 without, and 16 of 40 with
   * plain requests in its place (evidence-208.md §3e, the reviewer's SC-032 section). This file
   * runs exactly when an update installs — on the first screen after a launch — so it was the one
   * thing reliably making that happen. A waiting version is soon taken or applied by FR-013's
   * backstop, and the reload that follows runs the pass again.
   */
  readonly registration: ServiceWorkerRegistration;
}

function shouldContinue(pass: Pass): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible' && reachState() === 'reachable'
    && account.get()?.practitionerId === pass.startedFor
    && pass.registration.installing == null && pass.registration.waiting == null;
}

/**
 * Fetches every URL in `urls`, `CONCURRENCY` at a time, stopping — without throwing — the moment
 * `shouldContinue(pass)` says no. `onEach` receives the URL and its parsed body so a caller can pick
 * out the ones it needs (the three fixed list reads); every other URL's body is fetched only for its
 * side effect on the device (`core/stored-views/storing-client.ts` is what actually writes it).
 *
 * A single URL's own failure (a 404 for a container deleted since it was listed, a stalled read past
 * its three-second limit) is swallowed and the rest of the batch continues — this is a prefetch, and
 * a practitioner who never asked for it is owed no error for one page's failure to warm.
 */
async function fetchAll(
  pass: Pass, urls: readonly string[], onEach?: (url: string, body: unknown) => void,
): Promise<void> {
  let index = 0;
  async function worker(): Promise<void> {
    for (;;) {
      if (!shouldContinue(pass)) return;
      const i = index++;
      if (i >= urls.length) return;
      const url = urls[i]!;
      try {
        const body = await appClient().get<unknown>(url);
        onEach?.(url, body);
      } catch {
        // best-effort — see the file header.
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker));
}

/**
 * Sorts `items` most-recently-opened first, by `openedKey(kind, ref(item))` against `times`. An
 * item with no recorded time sorts AFTER every item that has one, in the ORIGINAL order among
 * themselves — `Array.prototype.sort` is a stable sort (ES2019+), which is what "pages never opened
 * come after, in the order the lists give" (FR-023) turns into here: two items both missing from
 * `times` compare equal and are therefore left exactly where the server's own list put them.
 */
function orderByOpened<T>(
  items: readonly T[],
  kind: OpenedKind,
  ref: (item: T) => number | string,
  times: ReadonlyMap<string, number>,
): readonly T[] {
  return [...items].sort((a, b) => {
    const ta = times.get(openedKey(kind, ref(a)));
    const tb = times.get(openedKey(kind, ref(b)));
    if (ta === undefined && tb === undefined) return 0;
    if (ta === undefined) return 1;
    if (tb === undefined) return -1;
    return tb - ta;
  });
}

/**
 * Builds the ordered list of every context, project and tag page's own URLs — "not hidden or
 * completed", most recently opened first. Exported for `fetch-ahead.spec.ts`'s own ordering tests,
 * which have no reason to also exercise the network side of `runFetchAheadOnce`.
 */
export function containerAndTagTargets(
  contexts: readonly ContextSummary[],
  projects: readonly ProjectSummary[],
  tags: readonly TagSummary[],
  times: ReadonlyMap<string, number>,
): readonly string[] {
  const openContexts = orderByOpened(contexts.filter((c) => c.isActive), 'context', (c) => c.id, times);
  const openProjects = orderByOpened(
    projects.filter((p) => p.state !== 'hidden' && p.state !== 'completed'), 'project', (p) => p.id, times,
  );
  const orderedTags = orderByOpened(tags, 'tag', (t) => t.name, times);

  return [
    ...openContexts.flatMap((c) => containerViewUrls('context', c.id)),
    ...openProjects.flatMap((p) => containerViewUrls('project', p.id)),
    ...orderedTags.flatMap((t) => tagViewUrls(t.name)),
  ];
}

/**
 * One full pass: the fixed reads (home's five plus the tags list — the tickler's own read is
 * byte-identical to home's `deferred` entry, per `homeViewUrls()`'s own header, so it is not fetched
 * a second time), then every context/project/tag page, most recently opened first.
 *
 * Exported for the two triggers below and for `fetch-ahead.spec.ts`; not exported for a screen to
 * call directly — nothing in this application asks for a prefetch on demand.
 */
export async function runFetchAheadOnce(): Promise<void> {
  if (!controllingServiceWorker()) return;
  if (disabledForMeasurement()) return;
  const practitionerId = account.get()?.practitionerId;
  if (practitionerId == null) return;
  const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (registration == null) return;
  const pass: Pass = { startedFor: practitionerId, registration };
  if (!shouldContinue(pass)) return;

  const fixed = [...homeViewUrls(), ...tagsListViewUrls()];
  const [contextsUrl, projectsUrl] = homeViewUrls();
  const [tagsUrl] = tagsListViewUrls();

  let contexts: readonly ContextSummary[] = [];
  let projects: readonly ProjectSummary[] = [];
  let tags: readonly TagSummary[] = [];

  await fetchAll(pass, fixed, (url, body) => {
    if (url === contextsUrl) contexts = (body as PagedContexts).items;
    else if (url === projectsUrl) projects = (body as PagedProjects).items;
    else if (url === tagsUrl) tags = body as TagSummary[];
  });

  if (!shouldContinue(pass)) return;

  const times = await openedTimes(practitionerId).catch(() => new Map<string, number>());
  await fetchAll(pass, containerAndTagTargets(contexts, projects, tags, times));
}

let watching = false;
let running = false;
let unsubscribeSignedIn: (() => void) | null = null;

function idleCallback(): (run: () => void) => void {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    return (run) => { window.requestIdleCallback(run); };
  }
  return (run) => { setTimeout(run, 0); };
}

function runGuarded(): void {
  if (running) return;
  running = true;
  idleCallback()(() => {
    void runFetchAheadOnce().finally(() => { running = false; });
  });
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible' && reachState() === 'reachable') runGuarded();
}

/**
 * Wires the two triggers (R-010's own scope note, and this file's header). Idempotent, the same
 * reason `startOutboxWatch()` is: called once from `main.tsx`, never from a component, and
 * `StrictMode` double-invoking a mount effect must never register the listener twice.
 */
export function startFetchAhead(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  unsubscribeSignedIn = onSignedIn(runGuarded);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

/** Test seam, like every other module-level store's reset. Production code never calls it. */
export function resetFetchAhead(): void {
  watching = false;
  running = false;
  unsubscribeSignedIn?.();
  unsubscribeSignedIn = null;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibilityChange);
}
